"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleHelp as HelpCircle, MessageSquare, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface FaqItem {
  id: string;
  question: string;
  schemaAnswer: string;
  tag: string;
  renderAnswer: () => React.ReactNode;
}
const faqData: FaqItem[] = [{
  id: "faq-1",
  question: "What does a B2B SEO consultant do?",
  schemaAnswer: "A B2B SEO consultant aligns search visibility with complex commercial buying journeys. I research buyer intent across multiple stakeholders, audit technical health, build content frameworks for procurement and evaluation stages, and optimise landing pages to deliver qualified sales enquiries rather than casual web visits.",
  tag: "Consultant Role",
  renderAnswer: () => <p>
        A B2B SEO consultant aligns search visibility with complex commercial buying journeys. I research buyer intent across multiple stakeholders, audit technical health, build content frameworks for procurement and evaluation stages, and optimise landing pages to deliver qualified sales enquiries rather than casual web visits.
      </p>
}, {
  id: "faq-2",
  question: "How is B2B SEO different from B2C SEO?",
  schemaAnswer: "B2B search involves longer sales cycles, multiple decision-makers, and considered evaluation rather than impulse purchasing. Consumer SEO typically pursues high-volume emotional searches, whereas B2B SEO focuses on commercial intent, technical depth, and queries that connect directly with prospective business buyers.",
  tag: "B2B vs B2C",
  renderAnswer: () => <p>
        B2B search involves longer sales cycles, multiple decision-makers, and considered evaluation rather than impulse purchasing. Consumer SEO typically pursues high-volume emotional searches, whereas B2B SEO focuses on commercial intent, technical depth, and queries that connect directly with prospective business buyers.
      </p>
}, {
  id: "faq-3",
  question: "Can SEO generate qualified B2B leads?",
  schemaAnswer: "Yes, when strategy targets search queries mapped to business problems, software evaluations, and service procurement. By refining landing page messaging, enquiry pathways, and technical crawlability, organic search attracts decision-makers actively looking for solutions and turns visits into actionable pipeline opportunities.",
  tag: "Lead Generation",
  renderAnswer: () => <p>
        Yes, when strategy targets search queries mapped to business problems, software evaluations, and service procurement. By refining landing page messaging, enquiry pathways, and technical crawlability, organic search attracts decision-makers actively looking for solutions and turns visits into actionable pipeline opportunities.
      </p>
}, {
  id: "faq-4",
  question: "How long does B2B SEO take?",
  schemaAnswer: "The time needed depends on your current site health, competitive landscape, domain authority, and internal sales cycle length. Technical corrections and search visibility typically move before enquiries and pipeline contribution develop. I review these variables and set realistic expectations following an initial SEO audit.",
  tag: "Timeframe & Expectations",
  renderAnswer: () => <p>
        The time needed depends on your current site health, competitive landscape, domain authority, and internal sales cycle length. Technical corrections and search visibility typically move before enquiries and pipeline contribution develop. I review these variables and set realistic expectations following an initial{" "}
        <Link to="/SeoAuditsPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
          SEO audit
        </Link>
        .
      </p>
}, {
  id: "faq-5",
  question: "What B2B SEO services do you provide?",
  schemaAnswer: "I provide an integrated consulting engagement covering buyer-intent keyword research, technical architecture, content strategy for multi-stakeholder journeys, commercial landing page optimisation, and search performance reporting. Every stream is managed directly by me to ensure strategic consistency.",
  tag: "Services Included",
  renderAnswer: () => <p>
        I provide an integrated consulting engagement covering buyer-intent keyword research, technical architecture, content strategy for multi-stakeholder journeys, commercial landing page optimisation, and search performance reporting. Every stream is managed directly by me to ensure strategic consistency.
      </p>
}, {
  id: "faq-6",
  question: "How do you research B2B keywords?",
  schemaAnswer: "I map search queries against the specific steps of your buyer journey, combining search landscape analysis with insights from your sales and marketing discussions. Rather than chasing generic search volume, I prioritise terms reflecting commercial problem-solving, product comparisons, and supplier shortlisting.",
  tag: "Keyword Research",
  renderAnswer: () => <p>
        I map search queries against the specific steps of your buyer journey, combining search landscape analysis with insights from your sales and marketing discussions. Rather than chasing generic search volume, I prioritise terms reflecting commercial problem-solving, product comparisons, and supplier shortlisting.
      </p>
}, {
  id: "faq-7",
  question: "Is SEO suitable for small B2B businesses?",
  schemaAnswer: "Yes. Smaller B2B firms often benefit significantly by competing on niche commercial queries and high-intent long-tail topics rather than broad category terms. Concentrating on well-structured service pages and distinct industry expertise allows smaller teams to capture relevant decision-makers efficiently.",
  tag: "Small Business Fit",
  renderAnswer: () => <p>
        Yes. Smaller B2B firms often benefit significantly by competing on niche commercial queries and high-intent long-tail topics rather than broad category terms. Concentrating on well-structured service pages and distinct industry expertise allows smaller teams to capture relevant decision-makers efficiently.
      </p>
}, {
  id: "faq-8",
  question: "How do you measure B2B SEO success?",
  schemaAnswer: "I evaluate progress through commercial search visibility, qualified enquiries, demo requests, and pipeline contribution rather than vanity traffic numbers. Tracking focus remains on whether search is connecting your business with the right organisations and driving genuine commercial discussions.",
  tag: "Measurement & Reporting",
  renderAnswer: () => <p>
        I evaluate progress through commercial search visibility, qualified enquiries, demo requests, and pipeline contribution rather than vanity traffic numbers. Tracking focus remains on whether search is connecting your business with the right organisations and driving genuine commercial discussions.
      </p>
}, {
  id: "faq-9",
  question: "Can you work with international B2B companies?",
  schemaAnswer: "Yes. I work with B2B companies across the UK as well as international English-speaking markets. When expanding across borders, I structure international SEO frameworks including regional targeting, international technical architecture, and market-specific search intent.",
  tag: "International Scope",
  renderAnswer: () => <p>
        Yes. I work with B2B companies across the UK as well as international English-speaking markets. When expanding across borders, I structure{" "}
        <Link to="/InternationalSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
          International SEO
        </Link>{" "}
        frameworks including regional targeting, international technical architecture, and market-specific search intent.
      </p>
}, {
  id: "faq-10",
  question: "How much does B2B SEO consultancy cost?",
  schemaAnswer: "Consultancy fees depend on the complexity of your website, current technical standing, and the depth of market competition. I scope engagements individually and discuss exact requirements during an introductory consultation, offering structured support through monthly SEO services.",
  tag: "Pricing & Scope",
  renderAnswer: () => <p>
        Consultancy fees depend on the complexity of your website, current technical standing, and the depth of market competition. I scope engagements individually and discuss exact requirements during an introductory consultation, offering structured support through{" "}
        <Link to="/MonthlySeoServicesPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
          Monthly SEO Services
        </Link>
        .
      </p>
}, {
  id: "faq-11",
  question: "Does technical SEO matter for B2B websites?",
  schemaAnswer: "Technical SEO is critical for B2B websites. If search engines encounter indexing obstacles, slow template rendering, or convoluted site taxonomy, valuable commercial pages will struggle to rank. Ensuring clean crawl paths and responsive page performance gives your buyer-focused content the foundation it requires.",
  tag: "Technical Foundations",
  renderAnswer: () => <p>
        Technical SEO is critical for B2B websites. If search engines encounter indexing obstacles, slow template rendering, or convoluted site taxonomy, valuable commercial pages will struggle to rank. I guide this work through tailored{" "}
        <Link to="/TechnicalSeoPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
          Technical SEO
        </Link>{" "}
        recommendations that give your buyer-focused content the foundation it requires.
      </p>
}, {
  id: "faq-12",
  question: "Can AI search optimisation support B2B SEO?",
  schemaAnswer: "Yes. Modern buyers increasingly consult AI-driven platforms and conversational overviews during early solution research. Structuring authoritative service details, clear definitions, and logical page hierarchy supports both traditional search crawlers and AI search optimisation, although no consultant can guarantee specific AI citations.",
  tag: "AI Search Visibility",
  renderAnswer: () => <p>
        Yes. Modern buyers increasingly consult AI-driven platforms and conversational overviews during early solution research. Structuring authoritative service details, clear definitions, and logical page hierarchy supports both traditional search crawlers and{" "}
        <Link to="/AISearchOptimisationPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
          AI Search Optimisation
        </Link>
        , although no consultant can guarantee specific AI citations.
      </p>
}];
export default function Expectations() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.schemaAnswer
      }
    }))
  };
  return <section id="faqconsultancyexpectations" data-nav="light" className="bg-background text-foreground relative py-20 lg:py-28 overflow-hidden">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary bg-secondary border-none">
              <ShieldCheck className="w-4 h-4 text-primary" />
              Honest &amp; Transparent Search Expertise
            </Badge>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-4">
            Frequently asked questions about B2B SEO consulting
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-default">
            These are the questions B2B decision-makers ask most when considering a B2B SEO consultant: how buyer intent shapes which keywords matter, how organic search turns into qualified enquiries, and how commercial search visibility is measured. If yours is not covered, ask me directly.
          </p>
        </motion.div>

        {/* Content Split */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Direct Access Callout */}
          <motion.div variants={itemVariants} className="lg:col-span-4 space-y-6">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-2xl -z-10" />
              
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                <HelpCircle className="w-6 h-6" />
              </div>

              <h3 className="text-xl md:text-2xl font-semibold text-card-foreground font-default mb-3">
                Direct Consultant Relationship
              </h3>

              <p className="text-muted-foreground text-base leading-relaxed mb-6">
                You communicate directly with me on every question, strategy milestone, and deliverable—ensuring complete personal accountability and strategic ownership.
              </p>

              <div className="space-y-3 pt-2 border-t border-border">
                <div className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Direct access to me</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Plain-English reporting</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>No guaranteed rankings or leads</span>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground mb-4">
                  Have a custom question about your search presence?
                </p>
                <Button asChild variant="outline" className="w-full justify-center gap-2 rounded-lg text-base font-medium">
                  <Link to="/ContactPage">
                    <MessageSquare className="w-4 h-4" />
                    <span>Ask Me Directly</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: FAQ Accordion */}
          <motion.div variants={itemVariants} className="lg:col-span-8">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-sm">
              <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-4">
                {faqData.map((item, i) => <AccordionItem data-index={i} key={item.id} value={item.id} className="border border-border/80 rounded-lg px-4 md:px-5 transition-colors data-[state=open]:bg-muted/30 data-[state=open]:border-primary/30">
                    <AccordionTrigger className="py-4 hover:no-underline text-base md:text-lg font-semibold text-foreground flex items-start text-left gap-3">
                      <span className="flex-1 font-default leading-snug">
                        {item.question}
                      </span>
                      <Badge variant="outline" className="hidden sm:inline-flex text-xs uppercase font-mono tracking-wider text-muted-foreground border-border shrink-0 ml-2">
                        {item.tag}
                      </Badge>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5 pt-1 font-default">
                      {item.renderAnswer()}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom CTA Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-16 bg-muted/50 border border-border rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-semibold text-foreground font-default">
              Ready to discuss your business goals?
            </h3>
            <p className="text-muted-foreground text-base max-w-xl">
              Book a 1-on-1 strategy session to analyse your current search visibility and outline a realistic growth roadmap.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 text-base font-medium shadow-sm">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            
            <Button asChild variant="ghost" size="lg" className="w-full sm:w-auto text-foreground hover:bg-background/80 rounded-lg text-base font-medium">
              <Link to="/CaseStudiesPage">
                <span>Explore Case Studies</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}